import ArticleIcon from '@mui/icons-material/Article';
import PhotoIcon from '@mui/icons-material/Photo';
import { motion } from 'motion/react';
import { ReactElement, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactionStats, ReactionTypes } from 'api';
import { useCreatePostReactions } from 'api/mutations/useCreatePostReactions';
import { useDeletePostReaction } from 'api/mutations/useDeletePostReaction';
import { ReactComponent as CommentIcon } from 'assets/icons/comments2.svg';
import { theme } from 'styles';

import { ReactionInline } from './ReactionInline';
import {
  ReactionButtonsContainer,
  CounterContainer,
  Reaction,
  Separator,
  ReactionWrapper,
  LeftGroup,
  ExpandText,
} from './styled';

interface ReactionWrapperProps {
  handleOpenReactionModal?: () => void;
  handlePostClick?: () => void;
  id: string;
  reactions?: ReactionStats;
  isPostDetails?: boolean;
  isPreview?: boolean;
  hideAttachments?: boolean;
  isAutoPost?: boolean;
  comments_count?: number;
  attachments_count?: number;
  mediaCount?: number;
  docCount?: number;
}

export function ReactionsWrapper({
  id,
  reactions,
  comments_count,
  mediaCount,
  docCount,
  handlePostClick,
  handleOpenReactionModal = () => null,
  isPreview = true,
  hideAttachments = false,
}: ReactionWrapperProps): ReactElement {
  const [reaction, setReaction] = useState<ReactionTypes | null>(
    reactions?.active_reaction || null,
  );
  const { t } = useTranslation();

  useLayoutEffect(() => {
    setReaction(reactions?.active_reaction || null);
  }, [reactions?.active_reaction]);

  const { deletePostReaction } = useDeletePostReaction();
  const { createPostReaction } = useCreatePostReactions();

  return (
    <>
      <Separator />

      <ReactionWrapper>
        <LeftGroup>
          <ReactionButtonsContainer>
            <Reaction
              onClick={(e: { stopPropagation: () => void }) => {
                e.stopPropagation();
                if (reaction) {
                  deletePostReaction({ postId: id });
                  setReaction(null);
                } else {
                  createPostReaction({ postId: id, reaction: ReactionTypes.HEART });
                  setReaction(ReactionTypes.HEART);
                }
              }}
            >
              <motion.div
                animate={{ y: ['-200%', '0%'] }}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.8 }}
                style={{ display: 'flex', flex: 1, alignItems: 'center' }}
              >
                <ReactionInline type={reaction} />
              </motion.div>
            </Reaction>
            <CounterContainer onClick={handleOpenReactionModal}>
              {reactions?.total}
            </CounterContainer>
          </ReactionButtonsContainer>

          {!isPreview && (
            <ReactionButtonsContainer>
              <motion.div
                animate={{ y: ['-200%', '0%'] }}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.8 }}
                style={{ display: 'flex', flex: 1, alignItems: 'center' }}
              >
                <CommentIcon
                  onClick={handlePostClick}
                  style={{
                    width: '20px',
                    height: '20px',
                    color: comments_count ? theme.palette.info.main : theme.palette.primary.grey,
                  }}
                />
              </motion.div>
              <CounterContainer>{comments_count}</CounterContainer>
            </ReactionButtonsContainer>
          )}
          {hideAttachments && (
            <>
              {!isPreview && !!docCount && (
                <ReactionButtonsContainer>
                  <motion.div
                    animate={{ y: ['-200%', '0%'] }}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.8 }}
                    style={{ display: 'flex', flex: 1, alignItems: 'center' }}
                  >
                    <ArticleIcon
                      style={{
                        width: '20px',
                        height: '20px',
                        color: docCount ? theme.palette.info.main : theme.palette.primary.grey,
                      }}
                      onClick={handlePostClick}
                    />
                  </motion.div>
                  <CounterContainer>{docCount}</CounterContainer>
                </ReactionButtonsContainer>
              )}
              {!isPreview && !!mediaCount && (
                <ReactionButtonsContainer>
                  <motion.div
                    animate={{ y: ['-200%', '0%'] }}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.8 }}
                    style={{ display: 'flex', flex: 1, alignItems: 'center' }}
                  >
                    <PhotoIcon
                      style={{
                        width: '20px',
                        height: '20px',
                        color: mediaCount ? theme.palette.info.main : theme.palette.primary.grey,
                      }}
                      onClick={handlePostClick}
                    />
                  </motion.div>
                  <CounterContainer>{mediaCount}</CounterContainer>
                </ReactionButtonsContainer>
              )}
            </>
          )}
        </LeftGroup>

        {!isPreview && <ExpandText onClick={handlePostClick}>{t('reactions.expand')}</ExpandText>}
      </ReactionWrapper>
    </>
  );
}
